<template lang="pug">
//- this template has a left/right layout and either side can be the sticky side.
//- We manually control the audio playback.
//- Either side can also use the edge style option
.screen__left(
  :class="[{ 'screen--sticky': stickyLeft }, { 'screen__left-edge': hasLeftEdge }]"
  ref="elRefLeft"
  v-if="screen.left || hasLeftEdge"
)
  //- audio marker added for the sticky side - includes all screens audio
  audio-marker(:name="`${screen.id}_left_0`" v-if="stickyLeft")

  template(
    v-for="(s, i) in screen.left ?? hasLeftEdge"
  )
    //- audio markers are added for the non-sticky (scrolling) side per screen
    audio-marker(:name="`${screen.id}_left_${i}`" v-if="stickyRight")

    uc-screen(
      :edit-path="hasLeftEdge ? `${editPath}.left-edge.${i}` : `${editPath}.left.${i}`"
      :nested="true"
      :screen="s"
    )

.screen__right(
  :class="[{ 'screen--sticky': stickyRight }, { 'screen__right-edge': hasRightEdge }]"
  ref="elRefRight"
  v-if="screen.right || hasRightEdge"
)
  //- audio marker added for the sticky side - includes all screens audio
  audio-marker(:name="`${screen.id}_right_0`" v-if="stickyRight")

  template(
    v-for="(s, i) in screen.right ?? hasRightEdge"
  )
    //- audio markers are added for the non-sticky (scrolling) side per screen
    audio-marker(:name="`${screen.id}_right_${i}`" v-if="stickyLeft")

    uc-screen(
      :edit-path="hasRightEdge ? `${editPath}.right-edge.${i}` : `${editPath}.right.${i}`"
      :nested="true"
      :screen="s"
    )

</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, type Ref } from 'vue';
import useAudio from '../stores/useAudio';
import { nowPlaying, play, queue, whenCurrentEnds } from '../lib/audio';
import {
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';
import ScrollTrigger from 'gsap/ScrollTrigger';
import bugAlert from '../lib/bugAlert';

const props = defineProps<{
  editPath: string;
  screen: UcScreen;
}>();

// eslint-disable-next-line no-unused-vars
const stickyLeft = computed(() => props.screen.template === 'sticky-left');
const stickyRight = computed(() => props.screen.template === 'sticky-right');
const hasLeftEdge = computed(() => props.screen['left-edge']);
const hasRightEdge = computed(() => props.screen['right-edge']);

// eslint-disable-next-line no-unused-vars
const { getAudioURL } = useAudio();
const { resolveChildIntro, audioAiJitEnabled } = useChildScreenAudio();

// Only play/queue narration while this sticky screen is the active section.
// Without this, ScrollTrigger callbacks fire for off-screen children during the
// initial layout settle (children momentarily stacked near the top), causing a
// burst of playback for later screens while the learner is still on an earlier one.
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));

const elRefLeft = ref<HTMLElement>();
const elRefRight = ref<HTMLElement>();
const elRefScroll = ref<HTMLElement>(stickyLeft.value ? elRefRight : elRefLeft);

// eslint-disable-next-line no-unused-vars
const getScreenAudio = (side: string, i: number) => {
  return getAudioURL(`${props.screen.id}_${side}_${i}`);
};

/**
 * Synthesise a stable JiT cache key for a child screen that may not have its
 * own YAML `id`. Uses the same `${parentId}_${side}_${i}` convention as the
 * sticky audio-marker names, so JiT keys align with the S3 marker scheme.
 */
const withStickyId = (
  childScreen: NarratableScreen,
  side: string,
  index: number
): NarratableScreen =>
  childScreen.id
    ? childScreen
    : { ...childScreen, id: `${props.screen.id}_${side}_${index}` };

/**
 * Play the intro audio for a sticky child screen.
 *
 * JiT path: resolves text via the child screen's schema.
 * S3  path: uses the sticky audio-marker convention `${screen.id}_${side}_${i}`.
 */
const playChildAudio = async (
  side: string,
  index: number,
  childScreen: NarratableScreen | undefined
) => {
  if (audioAiJitEnabled.value && childScreen) {
    const url = await resolveChildIntro(withStickyId(childScreen, side, index));
    if (url) {
      play(url);
      return true;
    } else {
      return false;
    }
  } else {
    const url = getScreenAudio(side, index);
    if (url) {
      play(url);
      return true;
    }
    return false;
  }
};

/** Queue the intro audio for a sticky child screen (same tier logic as playChildAudio). */
const queueChildAudio = async (
  side: string,
  index: number,
  childScreen: NarratableScreen | undefined
) => {
  if (audioAiJitEnabled.value && childScreen) {
    const url = await resolveChildIntro(withStickyId(childScreen, side, index));
    if (url) queue(url);
  } else {
    const url = getScreenAudio(side, index);
    if (url) queue(url);
  }
};

/**
 * Watch for the left and right elements to be available and set up the scroll triggers for each side's screens.
 * If the left side is sticky, we need to set up a scroll trigger to play left audio content first and queue the first right screen.
 * Otherwise we play the right screen audio as each scroll into view
 */
watch([elRefLeft, elRefRight], async () => {
  if (elRefLeft.value && elRefRight.value) {
    const leftScreens = (props.screen.left ?? hasLeftEdge.value) as
      | NarratableScreen[]
      | undefined;
    const rightScreens = (props.screen.right ?? hasRightEdge.value) as
      | NarratableScreen[]
      | undefined;

    const scroller = elRefScroll.value.closest('.scroll-page__content');

    // check if a single screen-level S3 audio file exists (backward compatibility)
    const screenLevelAudio = getAudioURL(props.screen.id);
    const response =
      !audioAiJitEnabled.value && screenLevelAudio
        ? await fetch(screenLevelAudio, { method: 'HEAD' }).catch(() => false)
        : false;

    if (response && response.ok) {
      // Backward compat: a pre-recorded file covers the whole sticky screen
      const parentScreen = elRefScroll.value.closest('.screen');
      ScrollTrigger.create({
        trigger: parentScreen,
        scroller,
        start: 'top 50%',
        end: 'bottom 50%',
        onToggle(self) {
          if (!sectionActive.value) return;
          if (self.isActive && screenLevelAudio) {
            play(screenLevelAudio);
          }
        }
      });
    } else if (stickyLeft.value || stickyRight.value) {
      // sticky-left or sticky-right: play the left or right panel's intro, queue the first right or left screen
      const parentScreen = elRefScroll.value.closest('.screen');
      ScrollTrigger.create({
        trigger: parentScreen,
        scroller,
        start: 'top 50%',
        end: 'bottom 50%',
        async onToggle(self) {
          if (!self.isActive) return;
          if (!sectionActive.value) return;
          const playSide = stickyLeft.value ? 'left' : 'right';
          const queueSide = stickyLeft.value ? 'right' : 'left';
          const playSideSuccess =
            (await playChildAudio(
              playSide,
              0,
              stickyLeft.value ? leftScreens?.[0] : rightScreens?.[0]
            )) ?? false;

          // if the play side failed, try to play the queue side (rather than queueing it)
          const queueParams = stickyLeft.value
            ? rightScreens?.[0]
            : leftScreens?.[0];
          if (!playSideSuccess) {
            await playChildAudio(queueSide, 0, queueParams);
          } else {
            await queueChildAudio(queueSide, 0, queueParams);
          }
        }
      });
    }

    // Per-screen scroll triggers for the scrolling side
    if (!response || !response.ok) {
      const scrollingSide = stickyLeft.value ? 'right' : 'left';
      const scrollingScreens = stickyLeft.value ? rightScreens : leftScreens;
      const children = elRefScroll.value?.querySelectorAll(':scope > .screen');

      children?.forEach((d: HTMLElement, i) => {
        const childScreen = scrollingScreens?.[i];
        ScrollTrigger.create({
          trigger: d,
          scroller,
          start: 'top 50%',
          end: 'bottom 50%',
          async onEnter() {
            // index 0 on sticky-left is handled by the top-level toggle above
            if (i === 0 && (stickyLeft.value || stickyRight.value)) return;
            if (!sectionActive.value) return;
            await playChildAudio(scrollingSide, i, childScreen);
          },
          async onEnterBack() {
            if (!sectionActive.value) return;
            await playChildAudio(scrollingSide, i, childScreen);
          }
        });
      });
    }
  }
});

onMounted(() => {
  // warn if layouts are not compatible
  if (props.screen.left && hasLeftEdge.value) {
    bugAlert(
      `${props.screen.id}: screen has both left and left-edge. Only supports one type.`
    );
  } else if (props.screen.right && hasRightEdge.value) {
    bugAlert(
      `${props.screen.id}: screen has both right and right-edge. Only supports one type.`
    );
  }
});
</script>

<style scoped>
.screen--sticky {
  @apply relative md_sticky md_h-screen bottom-0 left-0 top-0 w-full;
}

.screen--sticky .prose {
  max-width: 50ch;
}

.sticky + .screen {
  margin-top: -100vh;
}
</style>
